/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import PaginatorComponent from "@/components/pagination/PaginatorComponent.vue";
import PDFComponent from "@/components/viewer/PDFComponent.vue";
import PristyIcon from "@/components/PristyIcon.vue";
import CopyOrMovePopup from "@/components/popup/CopyOrMovePopup.vue";
import TreeExplorer from "@/components/TreeExplorer.vue";
import EllipsisBreadcrumb from "@/components/breadcrumb/EllipsisBreadcrumb.vue";
import BreadCrumbAlfresco from "@/components/breadcrumb/BreadCrumbAlfresco.vue";
import ComplexBreadcrumb from "@/components/breadcrumb/ComplexBreadcrumb.vue";
import ContentTableView from "@/components/navigation/ContentTableView.vue";
import ContentListView from "@/components/navigation/ContentListView.vue";
import ContentCompactTableView from "@/components/navigation/ContentCompactTableView.vue";
import ContentGridView from "@/components/navigation/ContentGridView.vue";
import DynamicContentView from "@/components/navigation/DynamicContentView.vue";
import GhostNodeRow from "@/components/navigation/GhostNodeRow.vue";
import ContentFiltersHeader from "@/components/navigation/ContentFiltersHeader.vue";
import ImageViewer from "@/components/viewer/ImageViewer.vue";
import VideoPlayer from "@/components/viewer/VideoPlayer.vue";
import AudioPlayer from "@/components/viewer/AudioPlayer.vue";
import PreviewPanel from "@/components/navigation/PreviewPanel.vue";
import CreateWorkflowPopup from "@/components/popup/CreateWorkflowPopup.vue";
import ImportNewVersionPopup from "@/components/popup/ImportNewVersionPopup.vue";
import SelectionStatus from "@/components/SelectionStatus.vue";
import ShareLinkPopup from "@/components/popup/ShareLinkPopup.vue";
import MemberListPopup from "@/components/popup/MemberListPopup.vue";
import SendMailPopup from "@/components/popup/SendMailPopup.vue";
import LayoutSelector from "@/components/navigation/LayoutSelector.vue";
import ColumnTogglePanel from "@/components/navigation/ColumnTogglePanel.vue";
import ContentToolbar from "@/components/navigation/ContentToolbar.vue";
import SelectionBar from "@/components/navigation/SelectionBar.vue";
import HelpKeyMessage from "@/components/HelpKeyMessage.vue";
import MemberList from "@/components/users/MemberList.vue";
import ActionMenu from "@/components/menu/ActionMenu.vue";
import CustomToastContent from "@/components/toast/CustomToastContent.vue";
import UploadStatus from "@/components/UploadStatus.vue";
import UploadTargetInfo from "@/components/UploadTargetInfo.vue";
import FolderUpload from "@/components/FolderUpload.vue";
import ImportPopupWithWorker from "@/components/popup/ImportPopupWithWorker.vue";
import AddPairInConfigDialog from "@/components/config/AddPairInConfigDialog.vue";
import ConfigDatatable from "@/components/config/ConfigDatatable.vue";
import ConfigItemEditor from "@/components/config/ConfigItemEditor.vue";
import ConfigSplitView from "@/components/config/ConfigSplitView.vue";
import ConfigUpload from "@/components/config/ConfigUpload.vue";
import ListVersionConfig from "@/components/config/ListVersionConfig.vue";
import AddCriterionPopup from "@/components/search/AddCriterionPopup.vue";
import AdvancedSearchPanel from "@/components/search/AdvancedSearchPanel.vue";
import AdvancedViewPanel from "@/components/search/AdvancedViewPanel.vue";
import ExactSearch from "@/components/search/ExactSearch.vue";
import FavoriteViewMenu from "@/components/search/FavoriteViewMenu.vue";
import UserViewsFavoritesPanel from "@/components/search/UserViewsFavoritesPanel.vue";
import SaveViewPopup from "@/components/search/SaveViewPopup.vue";
import ViewResultsPanel from "@/components/search/ViewResultsPanel.vue";
import PdfToolsComponent from "@/components/pdfTools/PdfToolsComponent.vue";
import CodeMirrorEditor from "@/components/CodeMirrorEditor.vue";
import DiffViewerDialog from "@/components/DiffViewerDialog.vue";
import ArrayEditor from "@/components/config/ArrayEditor.vue";
import ConfigHealthBanner from "@/components/config/ConfigHealthBanner.vue";
import ErrorComponent from "@/components/ErrorComponent.vue";
import ChatPanel from "@/components/chat/ChatPanel.vue";
import ChatMessage from "@/components/chat/ChatMessage.vue";
import ChatInput from "@/components/chat/ChatInput.vue";
import ChatRoomList from "@/components/chat/ChatRoomList.vue";
import WorkspaceCard from "@/components/workspace/WorkspaceCard.vue";
import WorkspaceList from "@/components/workspace/WorkspaceList.vue";
import PredefinedViewsPanel from "@/components/views/PredefinedViewsPanel.vue";
import ViewList from "@/components/views/ViewList.vue";

// Dashboard Components
import DashboardComponent from "@/components/dashboard/DashboardComponent.vue";
import DashboardEditor from "@/components/dashboard/editor/DashboardEditor.vue";
import DashboardAdminPage from "@/components/dashboard/DashboardAdminPage.vue";
import VersionHistoryDialog from "@/components/dashboard/editor/VersionHistoryDialog.vue";
import IconPickerDialog from "@/components/IconPickerDialog.vue";

// Classification Components
import AdminClassificationLauncher from "@/components/classification/AdminClassificationLauncher.vue";
import ClassificationPlanList from "@/components/classification/ClassificationPlanList.vue";
import ClassificationPlanCreate from "@/components/classification/ClassificationPlanCreate.vue";
import AdminClassificationPage from "@/components/classification/AdminClassificationPage.vue";
import NatureForm from "@/components/classification/NatureForm.vue";
import FieldForm from "@/components/classification/FieldForm.vue";
import EmplacementForm from "@/components/classification/EmplacementForm.vue";
import UploadPlanPopup from "@/components/classification/UploadPlanPopup.vue";
import NatureSelector from "@/components/classification/NatureSelector.vue";
import MetadataFormFields from "@/components/classification/MetadataFormFields.vue";

// Layout Components
import AppLayout from "@/components/layouts/AppLayout.vue";
import AppSidebar from "@/components/layouts/AppSidebar.vue";
import AppTopbar from "@/components/layouts/AppTopbar.vue";
import AppSubmenu from "@/components/layouts/AppSubmenu.vue";
import AppFooter from "@/components/layouts/AppFooter.vue";
import AppMobileLayout from "@/components/layouts/AppMobileLayout.vue";
import SidebarProfileSection from "@/components/layouts/SidebarProfileSection.vue";

// Component Library Installation
const componentLibrary = {
  install(app) {
    app.component("PaginatorComponent", PaginatorComponent);
    app.component("PDFComponent", PDFComponent);
    app.component("PristyIcon", PristyIcon);
    app.component("TreeExplorer", TreeExplorer);
    app.component("CopyOrMovePopup", CopyOrMovePopup);
    app.component("EllipsisBreadcrumb", EllipsisBreadcrumb);
    app.component("BreadCrumbAlfresco", BreadCrumbAlfresco);
    app.component("ComplexBreadcrumb", ComplexBreadcrumb);
    app.component("ContentTableView", ContentTableView);
    app.component("ContentListView", ContentListView);
    app.component("ContentCompactTableView", ContentCompactTableView);
    app.component("ContentGridView", ContentGridView);
    app.component("DynamicContentView", DynamicContentView);
    app.component("ContentFiltersHeader", ContentFiltersHeader);
    app.component("ImageViewer", ImageViewer);
    app.component("VideoPlayer", VideoPlayer);
    app.component("AudioPlayer", AudioPlayer);
    app.component("PreviewPanel", PreviewPanel);
    app.component("CreateWorkflowPopup", CreateWorkflowPopup);
    app.component("ImportNewVersionPopup", ImportNewVersionPopup);
    app.component("ShareLinkPopup", ShareLinkPopup);
    app.component("MemberListPopup", MemberListPopup);
    app.component("SendMailPopup", SendMailPopup);
    app.component("LayoutSelector", LayoutSelector);
    app.component("ContentToolbar", ContentToolbar);
    app.component("SelectionBar", SelectionBar);
    app.component("HelpKeyMessage", HelpKeyMessage);
    app.component("SelectionStatus", SelectionStatus);
    app.component("MemberList", MemberList);
    app.component("ActionMenu", ActionMenu);
    app.component("CustomToastContent", CustomToastContent);
    app.component("UploadTargetInfo", UploadTargetInfo);
    app.component("FolderUpload", FolderUpload);
    app.component("ImportPopupWithWorker", ImportPopupWithWorker);
    app.component("UploadStatus", UploadStatus);
    app.component("AddPairInConfigDialog", AddPairInConfigDialog);
    app.component("ConfigDatatable", ConfigDatatable);
    app.component("ConfigItemEditor", ConfigItemEditor);
    app.component("ConfigSplitView", ConfigSplitView);
    app.component("ConfigUpload", ConfigUpload);
    app.component("ListVersionConfig", ListVersionConfig);
    app.component("AddCriterionPopup", AddCriterionPopup);
    app.component("AdvancedSearchPanel", AdvancedSearchPanel);
    app.component("AdvancedViewPanel", AdvancedViewPanel);
    app.component("ExactSearch", ExactSearch);
    app.component("FavoriteViewMenu", FavoriteViewMenu);
    app.component("UserViewsFavoritesPanel", UserViewsFavoritesPanel);
    app.component("SaveViewPopup", SaveViewPopup);
    app.component("ViewResultsPanel", ViewResultsPanel);
    app.component("PdfToolsComponent", PdfToolsComponent);
    app.component("CodeMirrorEditor", CodeMirrorEditor);
    app.component("DiffViewerDialog", DiffViewerDialog);
    app.component("ArrayEditor", ArrayEditor);
    app.component("ConfigHealthBanner", ConfigHealthBanner);
    app.component("ErrorComponent", ErrorComponent);
    app.component("WorkspaceCard", WorkspaceCard);
    app.component("WorkspaceList", WorkspaceList);
    // Layout Components
    app.component("AppLayout", AppLayout);
    app.component("AppSidebar", AppSidebar);
    app.component("AppTopbar", AppTopbar);
    app.component("AppSubmenu", AppSubmenu);
    app.component("AppFooter", AppFooter);
    app.component("AppMobileLayout", AppMobileLayout);
    app.component("SidebarProfileSection", SidebarProfileSection);
    app.component("ChatPanel", ChatPanel);
    app.component("ChatMessage", ChatMessage);
    app.component("ChatInput", ChatInput);
    app.component("ChatRoomList", ChatRoomList);
    // Dashboard Components
    app.component("DashboardComponent", DashboardComponent);
    app.component("DashboardEditor", DashboardEditor);
    app.component("DashboardAdminPage", DashboardAdminPage);
    app.component("VersionHistoryDialog", VersionHistoryDialog);
    app.component("IconPickerDialog", IconPickerDialog);
  },
};

// Component Exports
export {
  // Lib
  componentLibrary,
  //component
  PDFComponent,
  PristyIcon,
  PaginatorComponent,
  CopyOrMovePopup,
  TreeExplorer,
  EllipsisBreadcrumb,
  BreadCrumbAlfresco,
  ComplexBreadcrumb,
  ContentTableView,
  ContentListView,
  ContentCompactTableView,
  ContentGridView,
  DynamicContentView,
  GhostNodeRow,
  ContentFiltersHeader,
  ImageViewer,
  VideoPlayer,
  AudioPlayer,
  PreviewPanel,
  LayoutSelector,
  ColumnTogglePanel,
  ContentToolbar,
  SelectionBar,
  HelpKeyMessage,
  SelectionStatus,
  MemberList,
  ActionMenu,
  CustomToastContent,
  FolderUpload,
  UploadTargetInfo,
  UploadStatus,
  AddPairInConfigDialog,
  ConfigDatatable,
  ConfigItemEditor,
  ConfigSplitView,
  ConfigUpload,
  ListVersionConfig,
  AddCriterionPopup,
  AdvancedSearchPanel,
  AdvancedViewPanel,
  ExactSearch,
  FavoriteViewMenu,
  UserViewsFavoritesPanel,
  SaveViewPopup,
  ViewResultsPanel,
  PdfToolsComponent,
  CodeMirrorEditor,
  DiffViewerDialog,
  ArrayEditor,
  WorkspaceCard,
  WorkspaceList,
  PredefinedViewsPanel,
  ViewList,
  ConfigHealthBanner,
  //popup
  CreateWorkflowPopup,
  ImportNewVersionPopup,
  ShareLinkPopup,
  MemberListPopup,
  SendMailPopup,
  ImportPopupWithWorker,
  ErrorComponent,
  // Layout Components
  AppLayout,
  AppSidebar,
  AppTopbar,
  AppSubmenu,
  AppFooter,
  AppMobileLayout,
  SidebarProfileSection,
  // Chat
  ChatPanel,
  ChatMessage,
  ChatInput,
  ChatRoomList,
  // Dashboard
  DashboardComponent,
  DashboardEditor,
  DashboardAdminPage,
  VersionHistoryDialog,
  IconPickerDialog,
  // Classification
  AdminClassificationLauncher,
  ClassificationPlanList,
  ClassificationPlanCreate,
  AdminClassificationPage,
  NatureForm,
  FieldForm,
  EmplacementForm,
  UploadPlanPopup,
  NatureSelector,
  MetadataFormFields,
};

// Services
export { default as alfrescoFileService } from "@/services/AlfrescoFileService.js";
export { default as alfrescoNodeService } from "@/services/AlfrescoNodeService.js";
export { default as alfrescoPermissionService } from "@/services/AlfrescoPermissionService.js";
export { default as collaboraAccessService } from "@/services/CollaboraAccessService.js";
export { default as collabService } from "@/services/CollabService.js";
export { default as errorService } from "@/services/ErrorService.js";
export { default as favoriteService } from "@/services/FavoriteService.js";
export { default as formatService } from "@/services/FormatService.js";
export { default as iconeService } from "@/services/IconeService.js";
export { default as keyboardShortcutsService } from "@/services/KeyboardShortcutsService.js";
export { default as menuService } from "@/services/MenuService.js";
export { default as peopleService } from "@/services/PeopleService.js";
export { default as previewService } from "@/services/PreviewService.js";
export { default as translationService } from "@/services/TranslationService.js";
export { default as workflowService } from "@/services/WorkflowService.js";
export { default as workspaceService } from "@/services/WorkspaceService.js";
export { default as configService } from "@/services/ConfigService.js";
export { default as configHealthService } from "@/services/ConfigHealthService.js";
export { default as userViewService } from "@/services/UserViewService.js";
export { default as alfrescoModelService } from "@/services/AlfrescoModelService.js";
export {
  default as filingPlanService,
  NatureDocument,
  TypeDossier,
} from "@/services/FilingPlanService.js";
export { default as chatBackendService } from "@/services/ChatBackendService.js";
export { default as diffusionService } from "@/services/DiffusionService.js";
export { default as rocketChatService } from "@/services/RocketChatService.js";
export { default as rocketChatAuthService } from "@/services/RocketChatAuthService.js";
export { default as classificationService } from "@/services/ClassificationService.js";
export { default as classificationFileService } from "@/services/ClassificationFileService.js";
export { default as dashboardService } from "@/services/DashboardService.js";
export { default as viewConfigService } from "@/services/ViewConfigService.js";
export { default as aftsQueryBuilder } from "@/services/AftsQueryBuilder.js";
export { default as ghostMatcher } from "@/services/GhostMatcher.js";

// Plugins
export { default as vueKeyCloak } from "@/plugins/KeycloakPlugin.js";

// Internationalization
export { default as i18n } from "@/i18n";
export { messages } from "@/i18n/locales";

// API Exports
export * from "@/AlfrescoApi";

// Composables
export { useTheme } from "@/composables/useTheme.js";
export { useLayout, MENU_MODES, MENU_THEMES } from "@/composables/useLayout.js";
export { useAuth } from "@/composables/useAuth.js";

// Store Exports
export { useCollaboraStore } from "@/stores/collabora";
export { useCollabStore } from "@/stores/collab.js";
export { useConfigStore } from "@/stores/config";
export { useFavoritesStore } from "@/stores/favorites.js";
export { useMenuStore } from "@/stores/menu";
export { useNavigationStore } from "@/stores/navigation.js";
export { useSearchStore } from "@/stores/search";
export { useSelectionStore } from "@/stores/selection";
export { usePaginatorStore } from "@/stores/paginator";
export { useUserPreferencesStore } from "@/stores/userPreferences";
export { useUserStore } from "@/stores/user";
export { useScreenStore } from "@/stores/screen";
export { useUploadStore } from "@/stores/upload";
export { useUserViewStore } from "@/stores/userView.js";
export { useChatStore } from "@/stores/chat.js";
export { useTaskNotificationStore } from "@/stores/taskNotification.js";
export { useFilingPlanStore } from "@/stores/filingPlan.js";

// Pages Exports
export { default as LoginPage } from "@/pages/LoginPage.vue";
export { default as NotFoundPages } from "@/pages/NotFoundPages.vue";
export { default as ErrorPage } from "@/pages/ErrorPage.vue";

// Assert Exports
export { checkNotNull, checkString } from "@/directives/assert.js";

// Composables
export {
  useCodeMirror,
  getLanguageExtension,
  getBaseExtensions,
  createEditorView,
} from "@/composables/useCodeMirror.js";

export {
  useMarkdownPreview,
  renderMarkdown,
  sanitizeHtml,
  generatePreviewDocument,
  DEFAULT_PREVIEW_STYLES,
} from "@/composables/useMarkdownPreview.js";

export {
  useCollabWebSocket,
  ConnectionState,
} from "@/composables/useCollabWebSocket.js";

export {
  useCollabEditor,
  ROLE_EDITOR,
  ROLE_OBSERVER,
} from "@/composables/useCollabEditor.js";

export {
  useChatWebSocket,
  ConnectionState as ChatConnectionState,
} from "@/composables/useChatWebSocket.js";

export { useClassificationPlan } from "@/composables/useClassificationPlan.js";
export { useDragDrop } from "@/composables/useDragDrop.js";
export { useGridValidation } from "@/composables/useGridValidation.js";
export { useResize } from "@/composables/useResize.js";
export { useGhostNodes } from "@/composables/useGhostNodes.js";
export {
  resolveTemplate,
  extractVariables,
  parseFileName,
  deduplicateFileNames,
} from "@/utils/classificationPlanUtils.js";
